import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Braces, Copy, KeyRound, Plus, Radio, ScrollText } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { apiClient, type Project } from '../lib/api-client';
import { useToast } from '../hooks/useToast';

function formatTime(timestamp?: number | null): string {
  if (!timestamp) return 'No transmission received yet';
  return new Date(timestamp * 1000).toLocaleString();
}

function platformLabel(platform: string | null): string {
  if (!platform) return 'Application';
  return platform.replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function projectConnectionKey(dsn: string | null | undefined): string | null {
  if (!dsn) return null;
  try {
    return new URL(dsn).username || null;
  } catch {
    return null;
  }
}

export const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: '', platform: 'node', retention_days: '30' });
  const { success, error: showError } = useToast();

  useEffect(() => {
    void loadProjects();
  }, []);

  const summary = useMemo(() => ({
    keys: projects.reduce((total, project) => total + Number(project.active_key_count || 0), 0),
    records: projects.reduce((total, project) => total + Number(project.event_count || 0), 0),
  }), [projects]);

  async function loadProjects() {
    setLoading(true);
    try {
      const response = await apiClient.getProjects();
      setProjects(response.projects || []);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }

  async function createProject() {
    if (!projectForm.name.trim()) return;
    setBusy(true);
    try {
      await apiClient.createProject({
        name: projectForm.name.trim(),
        platform: projectForm.platform,
        retention_days: Number(projectForm.retention_days),
      });
      success('Project created. Its Node SDK connection key is ready.');
      setProjectForm({ name: '', platform: 'node', retention_days: '30' });
      setShowCreate(false);
      await loadProjects();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to create project');
    } finally {
      setBusy(false);
    }
  }

  async function copyConnectionKey(dsn: string | null | undefined) {
    const connectionKey = projectConnectionKey(dsn);
    if (!connectionKey) return;
    try {
      await navigator.clipboard.writeText(connectionKey);
      success('Node SDK connection key copied.');
    } catch {
      showError('Could not access the clipboard.');
    }
  }

  return (
    <AppShell>
      <div className="space-y-8">
        <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-300">
              <Radio className="h-3.5 w-3.5" /> Node SDK connections
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-950 dark:text-white">Projects</h1>
            <p className="mt-2 text-base text-muted-foreground">
              Create tenant-bound public connection keys for release-scoped Node v1 transmission.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2 shadow-sm">
            <Plus className="h-4 w-4" /> New project
          </Button>
        </header>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3" aria-label="Project summary">
          <SummaryCard icon={<Braces />} value={projects.length.toLocaleString()} label="Active projects" />
          <SummaryCard icon={<KeyRound />} value={summary.keys.toLocaleString()} label="Active connection keys" />
          <SummaryCard icon={<ScrollText />} value={summary.records.toLocaleString()} label="Transmission records" />
        </section>

        {loading ? (
          <div className="space-y-4" aria-label="Loading projects">
            {[0, 1].map((item) => <div key={item} className="h-60 animate-pulse rounded-2xl bg-white/70 dark:bg-gray-900/60" />)}
          </div>
        ) : projects.length === 0 ? (
          <Card className="border-dashed border-indigo-300 bg-white/80 py-12 text-center dark:border-indigo-800 dark:bg-gray-950/60">
            <CardContent className="mx-auto max-w-lg p-6">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"><Braces className="h-7 w-7" /></div>
              <h2 className="text-xl font-semibold">Create your first application project</h2>
              <p className="mt-2 text-sm text-muted-foreground">You will get an ingest-only Node SDK connection key, then register the application and opaque manifest in Transmission Control.</p>
              <Button onClick={() => setShowCreate(true)} className="mt-6 gap-2"><Plus className="h-4 w-4" /> Create project</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-5 xl:grid-cols-2">
            {projects.map((project) => (
              <Card key={project.id} className="overflow-hidden border-white/80 bg-white/90 shadow-sm backdrop-blur dark:border-white/10 dark:bg-gray-950/70">
                <CardHeader className="gap-4 border-b bg-gradient-to-r from-white to-indigo-50/70 p-5 dark:from-gray-950 dark:to-indigo-950/20 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-950 font-mono text-sm font-bold text-white dark:bg-white dark:text-gray-950">{'</>'}</div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2"><h2 className="text-lg font-semibold">{project.name}</h2><span className="rounded-full border bg-white px-2 py-0.5 text-xs text-muted-foreground dark:bg-gray-900">{platformLabel(project.platform)}</span></div>
                      <p className="mt-1 font-mono text-xs text-muted-foreground">{project.slug}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5 p-5">
                  <div className="grid grid-cols-3 gap-3">
                    <ProjectMetric label="Records" value={Number(project.event_count || 0).toLocaleString()} />
                    <ProjectMetric label="Active keys" value={Number(project.active_key_count || 0).toLocaleString()} />
                    <ProjectMetric label="Retention" value={`${project.retention_days}d`} />
                  </div>
                  <p className="text-xs text-muted-foreground">Last transmission: {formatTime(project.last_event_at)}</p>
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Node SDK connection key</p>
                      <button type="button" onClick={() => void copyConnectionKey(project.dsn)} className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-900 dark:text-indigo-300"><Copy className="h-3.5 w-3.5" /> Copy</button>
                    </div>
                    <code className="block overflow-x-auto rounded-lg border bg-gray-950 px-4 py-3 text-xs text-gray-100">{projectConnectionKey(project.dsn) || 'No active project key'}</code>
                    <div className="mt-3 flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                      <span>Ingest-only. It cannot upload manifests or activate a rule.</span>
                      <Link to="/transmission-control" className="font-semibold text-indigo-700 hover:text-indigo-900 dark:text-indigo-300">Transmission Control →</Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create an application project">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">A project receives an ingest-only public connection key. Transmission decisions are made separately per application release.</p>
          <Input label="Project name" placeholder="Checkout API" value={projectForm.name} onChange={(event) => setProjectForm((form) => ({ ...form, name: event.target.value }))} autoFocus />
          <Select label="Primary platform" value={projectForm.platform} onChange={(event) => setProjectForm((form) => ({ ...form, platform: event.target.value }))} options={[{ value: 'node', label: 'Node.js' }, { value: 'javascript', label: 'JavaScript / TypeScript' }, { value: 'cloudflare-workers', label: 'Cloudflare Workers' }]} />
          <Select label="Metadata retention" value={projectForm.retention_days} onChange={(event) => setProjectForm((form) => ({ ...form, retention_days: event.target.value }))} options={[{ value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: '1 year' }]} />
          <div className="flex justify-end gap-2 pt-3"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button disabled={busy || !projectForm.name.trim()} onClick={() => void createProject()}>{busy ? 'Creating…' : 'Create project'}</Button></div>
        </div>
      </Dialog>
    </AppShell>
  );
};

function SummaryCard({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return <Card className="border-white/70 bg-white/80 backdrop-blur dark:border-white/10 dark:bg-gray-950/60"><CardContent className="flex items-center gap-4 p-5"><div className="rounded-xl bg-indigo-100 p-3 text-indigo-700 [&>svg]:h-5 [&>svg]:w-5 dark:bg-indigo-950 dark:text-indigo-300">{icon}</div><div><p className="text-2xl font-bold">{value}</p><p className="text-sm text-muted-foreground">{label}</p></div></CardContent></Card>;
}

function ProjectMetric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border bg-gray-50/70 p-3 dark:bg-gray-900/50"><p className="text-lg font-semibold tabular-nums">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>;
}
